import React, {
  createContext,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { readSportsPageState } from '../pages/Sports/_helpers/sportsPageState';
import { toDisplayOddsDecimal } from '../pages/Sports/_helpers/oddsFormat';
import useServiceContextHook from '../_hooks/service.context.hook';
import {
  createSportsnewPlacementAccountLifecycle,
  normalizedSportsnewPlacementUserId,
} from '../_helpers/sportsnewPlacementAccountIdentity.owner';
import {
  isSportsBetCartTerminalSelectionMismatch,
  resolveSportsBetCartSelectionStatus,
  sportsBetCartTerminalSelectionReason,
} from './_helpers/sportsBetCartSelectionStatus';
import {
  reduceSportsBetCartBulkAcceptance,
} from './_helpers/sportsBetCartBulkAcceptance';

/** 베팅 카트 상태 관리 — 타입별 분리 */
export const SportsBetCartContext = createContext();

const EMPTY_CARTS = Object.freeze({
  DOMESTIC: Object.freeze([]),
  INTERNATIONAL: Object.freeze([]),
  SPECIAL: Object.freeze([]),
  LIVE: Object.freeze([]),
});
const initialSportsPageState = readSportsPageState();
const nextCartRevision = revision => (
  revision < Number.MAX_SAFE_INTEGER ? revision + 1 : revision
);

const cartOddStateIdOf = selection => selection?.odds_id;

const isSameCartId = (left, right) => {
  if (left == null || right == null) return false;

  const leftNumber = Number(left);
  const rightNumber = Number(right);
  if (Number.isFinite(leftNumber) && Number.isFinite(rightNumber)) {
    return leftNumber === rightNumber;
  }

  return String(left) === String(right);
};

const initialCartState = ownerTag => ({
  cartsByType: {
    DOMESTIC: [],
    INTERNATIONAL: [],
    SPECIAL: [],
    LIVE: [],
  },
  currentType: initialSportsPageState?.bettingType || 'DOMESTIC',
  stake: '',
  cartRevision: 0,
  ownerTag,
  cartApplyReceipt: null,
});

const sportsBetCartStateReducer = (state, action) => {
  switch (action.type) {
    case 'OWNER_CHANGED':
      if (action.ownerTag === state.ownerTag) return state;
      return {
        ...initialCartState(action.ownerTag),
        currentType: state.currentType,
        cartRevision: nextCartRevision(state.cartRevision),
      };
    case 'SET_CURRENT_TYPE': {
      const nextType = typeof action.value === 'function'
        ? action.value(state.currentType)
        : action.value;
      if (!Object.prototype.hasOwnProperty.call(EMPTY_CARTS, nextType) || nextType === state.currentType) {
        return state;
      }
      return {
        ...state,
        currentType: nextType,
        cartRevision: nextCartRevision(state.cartRevision),
        cartApplyReceipt: null,
      };
    }
    case 'SET_STAKE': {
      const nextStake = typeof action.value === 'function'
        ? action.value(state.stake)
        : action.value;
      const normalizedStake = nextStake == null ? '' : String(nextStake);
      if (normalizedStake === state.stake) return state;
      return {
        ...state,
        stake: normalizedStake,
        cartRevision: nextCartRevision(state.cartRevision),
        cartApplyReceipt: null,
      };
    }
    case 'UPDATE_CARTS': {
      const nextCarts = action.updater(state.cartsByType);
      if (!nextCarts || nextCarts === state.cartsByType) return state;
      return {
        ...state,
        cartsByType: nextCarts,
        cartRevision: nextCartRevision(state.cartRevision),
        cartApplyReceipt: null,
      };
    }
    case 'CLEAR_CURRENT_CART': {
      const current = state.cartsByType[state.currentType] || [];
      if (current.length === 0 && state.stake === '') return state;
      return {
        ...state,
        cartsByType: { ...state.cartsByType, [state.currentType]: [] },
        stake: '',
        cartRevision: nextCartRevision(state.cartRevision),
        cartApplyReceipt: null,
      };
    }
    case 'ACCEPT_BULK_CHANGES': {
      const reduced = reduceSportsBetCartBulkAcceptance({
        state: {
          selections: state.cartsByType[state.currentType] || [],
          stake: state.stake,
          revision: state.cartRevision,
          ownerTag: state.ownerTag,
          applyReceipt: state.cartApplyReceipt,
        },
        command: action.command,
      });
      if (!reduced || reduced.revision === state.cartRevision) return state;
      return {
        ...state,
        cartsByType: {
          ...state.cartsByType,
          [state.currentType]: reduced.selections,
        },
        stake: reduced.stake,
        cartRevision: reduced.revision,
        cartApplyReceipt: reduced.applyReceipt,
      };
    }
    case 'CLEAR_APPLY_RECEIPT': {
      const receipt = state.cartApplyReceipt;
      if (
        !receipt
        || receipt.request_id !== action.requestId
        || receipt.owner_tag !== action.ownerTag
        || receipt.committed_revision !== action.committedRevision
      ) return state;
      return { ...state, cartApplyReceipt: null };
    }
    default:
      return state;
  }
};

export const SportsBetCartProvider = ({ children }) => {
  const { session } = useServiceContextHook();
  const [accountLifecycle] = useState(createSportsnewPlacementAccountLifecycle);
  const [accountIdentity, setAccountIdentity] = useState(() => accountLifecycle.capture());
  const sessionUserId = normalizedSportsnewPlacementUserId(session?.user?.id);
  const captureAccountIdentity = useCallback(
    () => accountLifecycle.capture(),
    [accountLifecycle],
  );
  const isAccountIdentityCurrent = useCallback(
    guard => accountLifecycle.isCurrent(guard),
    [accountLifecycle],
  );
  const ownerTag = accountIdentity.accountKey;
  const [cartState, dispatchCartState] = useReducer(
    sportsBetCartStateReducer,
    ownerTag,
    initialCartState,
  );
  const cartStateRef = useRef(cartState);
  const cartsByTypeRef = useRef(cartState.cartsByType);
  const pendingBulkRequestRef = useRef(null);
  const claimedBulkReceiptIdsRef = useRef(new Set());
  const [oddsChanges, setOddsChanges] = useState({});
  const [bonusMultiplier, setBonusMultiplier] = useState(1);

  useLayoutEffect(() => {
    const committed = accountLifecycle.commit(sessionUserId);
    // Commit account identity before paint; deferring leaves the previous account cart interactive.
    setAccountIdentity(previous => (previous === committed ? previous : committed));
  }, [accountLifecycle, sessionUserId]);

  const {
    cartsByType,
    currentType,
    stake,
    cartRevision,
    cartApplyReceipt,
  } = cartState;
  const selections = cartsByType[currentType] || EMPTY_CARTS.DOMESTIC;

  useLayoutEffect(() => {
    cartStateRef.current = cartState;
    cartsByTypeRef.current = cartState.cartsByType;
    const pending = pendingBulkRequestRef.current;
    if (!pending) return;
    const receiptMatches = cartState.cartApplyReceipt?.request_id === pending.requestId
      && cartState.cartApplyReceipt?.owner_tag === pending.ownerTag;
    if (!receiptMatches && cartState.cartRevision !== pending.expectedRevision) {
      pendingBulkRequestRef.current = null;
    }
  }, [cartState]);

  useLayoutEffect(() => {
    dispatchCartState({ type: 'OWNER_CHANGED', ownerTag });
    // Clear owner-specific flashes within the same pre-paint account boundary.
    setOddsChanges({});
  }, [ownerTag]);

  const updateCartsByType = useCallback((updater) => {
    dispatchCartState({ type: 'UPDATE_CARTS', updater });
  }, []);

  const setCurrentType = useCallback((value) => {
    dispatchCartState({ type: 'SET_CURRENT_TYPE', value });
  }, []);

  const setStake = useCallback((value) => {
    dispatchCartState({ type: 'SET_STAKE', value });
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const matchIds = [...new Set(
      (cartsByType.LIVE || [])
        .map((selection) => Number(selection?.match_id ?? selection?.matchId))
        .filter((value) => Number.isFinite(value))
    )];
    window.dispatchEvent(new CustomEvent('sp_cart_match_ids', { detail: { matchIds } }));
  }, [cartsByType]);

  const otherTypeCounts = useMemo(() => {
    const counts = {};
    Object.entries(cartsByType).forEach(([type, items]) => {
      if (items.length > 0) counts[type] = items.length;
    });
    return counts;
  }, [cartsByType]);

  const toggleSelection = useCallback((sel) => {
    updateCartsByType(prev => {
      const normalizedSelection = {
        ...sel,
        odds: toDisplayOddsDecimal(sel?.odds) ?? Number(sel?.odds),
      };
      const cart = prev[currentType] || [];
      const exists = cart.find(s => isSameCartId(cartOddStateIdOf(s), cartOddStateIdOf(normalizedSelection)));
      if (exists) {
        return {
          ...prev,
          [currentType]: cart.filter(
            s => !isSameCartId(cartOddStateIdOf(s), cartOddStateIdOf(normalizedSelection)),
          ),
        };
      }
      const groupKey = normalizedSelection.market_group_key;
      const filtered = groupKey
        ? cart.filter(s => s.market_group_key !== groupKey)
        : cart.filter(
          s => !(s.market_id && isSameCartId(s.market_id, normalizedSelection.market_id)),
        );
      return { ...prev, [currentType]: [...filtered, normalizedSelection] };
    });
  }, [currentType, updateCartsByType]);

  const removeSelection = useCallback((oddsId) => {
    updateCartsByType(prev => {
      const cart = prev[currentType] || [];
      return {
        ...prev,
        [currentType]: cart.filter(s => !isSameCartId(cartOddStateIdOf(s), oddsId)),
      };
    });
  }, [currentType, updateCartsByType]);

  const clearCart = useCallback(() => {
    dispatchCartState({ type: 'CLEAR_CURRENT_CART' });
  }, []);

  const replaceSelectionsForType = useCallback((type, nextSelections = []) => {
    if (!Object.prototype.hasOwnProperty.call(EMPTY_CARTS, type)) return;
    updateCartsByType(prev => ({
      ...prev,
      [type]: Array.isArray(nextSelections) ? nextSelections : [],
    }));
  }, [updateCartsByType]);

  const isSelected = useCallback((oddsId) => (
    (cartsByType[currentType] || []).some(s => isSameCartId(cartOddStateIdOf(s), oddsId))
  ), [cartsByType, currentType]);

  const isSelectionInAnyCart = useCallback((oddsId) => (
    Object.values(cartsByTypeRef.current || {}).some((cart) => (
      (Array.isArray(cart) ? cart : []).some((selection) => isSameCartId(cartOddStateIdOf(selection), oddsId))
    ))
  ), []);

  /**
   * Keep accepted odds frozen until the explicit cart-wide acceptance command.
   * The reducer update remains pure; presentation flash is derived beforehand.
   */
  const updateOddsInCart = useCallback((oddsId, newOdds, patch = {}) => {
    let found = false;
    let flashDirection = null;
    const numNew = toDisplayOddsDecimal(newOdds);
    Object.values(cartsByTypeRef.current).forEach((cart) => {
      cart.forEach((selection) => {
        if (!isSameCartId(cartOddStateIdOf(selection), oddsId)) return;
        found = true;
        const oldOdds = Number(selection.odds);
        if (
          flashDirection == null
          && !isSportsBetCartTerminalSelectionMismatch(selection.reason)
          && Number.isFinite(numNew)
          && Math.abs(oldOdds - numNew) > 0.001
        ) {
          flashDirection = numNew > oldOdds ? 'up' : 'down';
        }
      });
    });
    if (!found) return false;

    if (flashDirection) {
      setOddsChanges(changes => ({ ...changes, [oddsId]: flashDirection }));
      setTimeout(() => {
        setOddsChanges(changes => {
          const next = { ...changes };
          delete next[oddsId];
          return next;
        });
      }, 2000);
    }

    updateCartsByType(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach((type) => {
        updated[type] = updated[type].map((selection) => {
          if (!isSameCartId(cartOddStateIdOf(selection), oddsId)) return selection;
          const oldOdds = Number(selection.odds);
          const terminalSelectionMismatch = isSportsBetCartTerminalSelectionMismatch(
            selection.reason,
          );
          const hasNewOdds = Number.isFinite(numNew);
          const priceChanged = !terminalSelectionMismatch
            && hasNewOdds
            && Math.abs(oldOdds - numNew) > 0.001;
          const nextStatus = resolveSportsBetCartSelectionStatus({
            currentStatus: selection.status ?? 'active',
            currentReason: selection.reason,
            pendingOdds: selection.pendingOdds,
            patch,
            priceChanged,
          });
          return {
            ...selection,
            ...patch,
            status: nextStatus,
            ...(terminalSelectionMismatch
              ? { reason: sportsBetCartTerminalSelectionReason(selection.reason) }
              : {}),
            pendingOdds: nextStatus === 'odds_changed' && hasNewOdds
              ? numNew
              : (nextStatus === 'active' ? undefined : selection.pendingOdds),
            odds: selection.odds,
          };
        });
      });
      return updated;
    });
    return true;
  }, [updateCartsByType]);

  const acceptCartChanges = useCallback((command = {}) => {
    const requestId = String(command.requestId || '');
    const commandOwnerTag = String(command.ownerTag || '');
    const expectedRevision = Number(command.expectedRevision);
    const snapshot = cartStateRef.current;
    if (
      !requestId
      || pendingBulkRequestRef.current
      || commandOwnerTag !== snapshot.ownerTag
      || !Number.isSafeInteger(expectedRevision)
      || expectedRevision !== snapshot.cartRevision
    ) return null;

    pendingBulkRequestRef.current = { requestId, ownerTag: commandOwnerTag, expectedRevision };
    dispatchCartState({
      type: 'ACCEPT_BULK_CHANGES',
      command: { requestId, ownerTag: commandOwnerTag, expectedRevision },
    });
    return requestId;
  }, []);

  const consumeCartApplyReceipt = useCallback(({
    requestId,
    ownerTag: requestedOwnerTag,
    committedRevision,
  } = {}) => {
    const receipt = cartStateRef.current.cartApplyReceipt;
    if (
      !receipt
      || receipt.request_id !== requestId
      || receipt.owner_tag !== requestedOwnerTag
      || receipt.committed_revision !== committedRevision
      || claimedBulkReceiptIdsRef.current.has(requestId)
    ) return null;

    while (claimedBulkReceiptIdsRef.current.size >= 128) {
      const oldest = claimedBulkReceiptIdsRef.current.values().next().value;
      claimedBulkReceiptIdsRef.current.delete(oldest);
    }
    claimedBulkReceiptIdsRef.current.add(requestId);
    if (pendingBulkRequestRef.current?.requestId === requestId) {
      pendingBulkRequestRef.current = null;
    }
    dispatchCartState({
      type: 'CLEAR_APPLY_RECEIPT',
      requestId,
      ownerTag: requestedOwnerTag,
      committedRevision,
    });
    return receipt;
  }, []);

  const totalOdds = useMemo(() => {
    if (selections.length === 0) return 0;
    return selections.reduce(
      (acc, selection) => acc * (toDisplayOddsDecimal(selection.odds) ?? 1),
      1,
    );
  }, [selections]);

  const potentialWinRaw = useMemo(() => {
    const numStake = Number(stake) || 0;
    const betType = selections.length <= 1 ? 'SINGLE' : 'COMBO';
    if (betType === 'SINGLE') {
      return selections.reduce((sum, selection) => sum + numStake * Number(selection.odds), 0);
    }
    return numStake * totalOdds;
  }, [selections, stake, totalOdds]);

  const potentialWin = useMemo(
    () => Math.floor(potentialWinRaw * bonusMultiplier),
    [potentialWinRaw, bonusMultiplier],
  );

  const value = {
    accountIdentity,
    captureAccountIdentity,
    isAccountIdentityCurrent,
    selections,
    stake,
    setStake,
    currentType,
    setCurrentType,
    otherTypeCounts,
    toggleSelection,
    removeSelection,
    clearCart,
    replaceSelectionsForType,
    isSelected,
    isSelectionInAnyCart,
    totalOdds,
    potentialWin,
    potentialWinRaw,
    bonusMultiplier,
    setBonusMultiplier,
    updateOddsInCart,
    oddsChanges,
    cartRevision,
    cartApplyReceipt,
    consumeCartApplyReceipt,
    acceptCartChanges,
  };

  return (
    <SportsBetCartContext.Provider value={value}>
      {children}
    </SportsBetCartContext.Provider>
  );
};
